import React from 'react';
import {
	Box,
	Divider,
	IconButton,
	ListItemIcon,
	Menu,
	MenuItem,
	Tooltip,
	Typography,
	useMediaQuery,
} from '@mui/material';
import {Theme} from '../types';
import useAuth from './Auth';
import KeyIcon from '@mui/icons-material/Key';
import { Link } from 'react-router-dom';
import { useThemeContext } from '../ThemeProvider';
import MaterialUISwitch from './MuiThemeSwitch';
import { useAppSelector } from '../hooks';
import { selectMyCompany } from '../containers/accountSlice';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from 'react-ga4';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CookieIcon from '@mui/icons-material/Cookie';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';

const AccountMenu = () => {
	const isSmScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const {logout, isAuthenticated, loginWithRedirect} = useAuth();
	const {toggleDarkMode, isDarkModeOn} = useThemeContext();
	const myCompany = useAppSelector(selectMyCompany);
	const { t } = useTranslation();
	const { cookieConsent, resetCookieConsent } = useCookieConsent();

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
		ReactPixel.trackCustom('AccountMenuClicked');
		ReactGA.event({
			category: 'UI',
			action: 'AccountMenuClicked',
		});
	};
	const handleClose = () => {
		setAnchorEl(null);
	};
	const handleLogoutClick = () => {
		logout();
	};
	const handleDarkModeClick = () => {
		toggleDarkMode();
		ReactPixel.trackCustom('DarkModeToggled', { darkMode: isDarkModeOn ? 'on' : 'off' });
		ReactGA.event({
			category: 'UI',
			action: `DarkModeToggled: ${isDarkModeOn ? 'on' : 'off'}`,
		});
	};
	const handleLoginClick = () => {
		loginWithRedirect();
		ReactPixel.trackCustom('LoginClicked');
		ReactGA.event({
			category: 'UI',
			action: 'LoginClicked',
		});
	};

	const handleCookiePreferencesClick = () => {
		resetCookieConsent();
		handleClose();
	};

	const logoSrc = isDarkModeOn ? '/logo-dark.svg' : '/logo.svg';

	return <>
		<Box display='flex' justifyContent='right' alignItems='center'>
			{!isSmScreen && <Typography variant='h6' sx={{mr: 1, userSelect: 'none'}}>{t('navigation.appName')}</Typography>}
			<Tooltip title={t('accountMenu.title')}>
				<IconButton
					onClick={handleClick}
					aria-controls={open ? 'account-menu' : undefined}
					aria-haspopup="true"
					aria-expanded={open ? 'true' : undefined}
				>
					<img src={logoSrc} width={isSmScreen ? 32 : 40} />
				</IconButton>
			</Tooltip>
		</Box>
		<Menu
			anchorEl={anchorEl}
			id="account-menu"
			open={open}
			onClose={handleClose}
		>
			{myCompany?.roleCustomer && <MenuItem component={Link} to='/purchases'>
				{t('accountMenu.orders')}
			</MenuItem>}
			{myCompany?.roleCreator && <MenuItem component={Link} to='/plans'>
				{t('accountMenu.plans')}
			</MenuItem>}
			{myCompany?.roleVendor && <MenuItem component={Link} to='/sales' sx={{gap: 1}}>
				<AttachMoneyIcon />
				{t('accountMenu.sales')}
			</MenuItem>}
			{myCompany?.roleVendor && <MenuItem component={Link} to='/build-kits' sx={{gap: 1}}>
				<LocalOfferIcon />
				{t('accountMenu.quickOrders')}
			</MenuItem>}
			{myCompany?.roleVendor && <MenuItem component={Link} to='/products' sx={{gap: 1}}>
				<FormatListBulletedIcon />
				{t('accountMenu.products')}
			</MenuItem>}
			{isAuthenticated && <MenuItem component={Link} to='/profile'>
				<ListItemIcon>
					<PersonIcon fontSize="small" />
				</ListItemIcon>
				{t('accountMenu.profile')} {myCompany?.profileId && ` (${myCompany?.profileId})`}
			</MenuItem>}
			{!isAuthenticated && <MenuItem 
				onClick={handleLoginClick}
				disabled={cookieConsent === false}
				title={cookieConsent === false ? t('accountMenu.loginDisabledTooltip') : ''}
			>
				<ListItemIcon>
					<KeyIcon fontSize="small"/>
				</ListItemIcon>
				{t('accountMenu.login')}
			</MenuItem>}
			<Divider />
			<MenuItem onClick={handleDarkModeClick}>
				<MaterialUISwitch checked={isDarkModeOn} />
				{t('accountMenu.switchMode')}
			</MenuItem>
			<MenuItem onClick={handleCookiePreferencesClick}>
				<ListItemIcon>
					<CookieIcon fontSize="small" />
				</ListItemIcon>
				{t('accountMenu.cookiePreferences')}
			</MenuItem>
			<Divider />
			<MenuItem component={Link} to='https://hello.diyfeed.com' target='_blank' rel="noopener noreferrer" sx={{gap: 1, py: 1}}>
				<img src='/logo.svg' width={20} style={{filter: isDarkModeOn ? 'invert(1)' : 'none'}} />
				{t('accountMenu.aboutDIYFeed')}
				<OpenInNewIcon fontSize="small" />
			</MenuItem>
			<MenuItem component={Link} to={t('common.termsOfServiceHref')} target='_blank' rel="noopener noreferrer">
				{t('accountMenu.termsOfService')}
			</MenuItem>
			<MenuItem component={Link} to={t('common.privacyPolicyHref')} target='_blank' rel="noopener noreferrer">
				{t('accountMenu.privacyPolicy')}
			</MenuItem>
			{isAuthenticated && <MenuItem onClick={handleLogoutClick}>
				<ListItemIcon>
					<LogoutIcon fontSize="small" />
				</ListItemIcon>
				{t('accountMenu.logout')}
			</MenuItem>}
		</Menu>
	</>;
};

export default AccountMenu;
